import { useState } from "react";
import { useUpdateUserProfileMutation } from "@/api/clubsApi";
import { usePublicKeys } from "@/components/providers/PublicKeysProvider";
import { Icon } from "@/components/ui/Icon";
import { cn } from "@/components/ui/cn";

interface OnboardingLocationStepProps {
  userId: string;
  setLocationPermissionGranted: (granted: boolean) => void;
  onContinue: () => void;
}

export function OnboardingLocationStep({
  userId,
  setLocationPermissionGranted,
  onContinue,
}: OnboardingLocationStepProps) {
  const [updateProfile] = useUpdateUserProfileMutation();
  const { keys } = usePublicKeys();
  const [isSavingLocation, setIsSavingLocation] = useState(false);

  const reverseLookup = async (latitude: number, longitude: number) => {
    const mapboxKey = keys?.mapbox_public_key;
    if (!mapboxKey) {
      console.error("[OnboardingLocation] Mapbox key not available");
      return;
    }
    try {
      const url = `https://api.mapbox.com/search/searchbox/v1/reverse?longitude=${longitude}&latitude=${latitude}&access_token=${mapboxKey}`;
      const res = await fetch(url);
      if (!res.ok) {
        console.error(
          "[OnboardingLocation] Mapbox reverse lookup failed:",
          res.status
        );
        return;
      }
      const data = await res.json();
      return data.features?.[0];
    } catch (err) {
      console.error(
        "[OnboardingLocation] Error retrieving location from Mapbox:",
        err
      );
    }
  };

  const fetchAndSaveLocation = async () => {
    if (!userId || !keys?.mapbox_public_key) return;

    try {
      setIsSavingLocation(true);

      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 10000,
          });
        }
      );

      const { latitude, longitude } = position.coords;
      const locationObject = await reverseLookup(latitude, longitude);

      if (locationObject) {
        const locationCity =
          locationObject.properties?.context?.place?.name;
        const locationState =
          locationObject.properties?.context?.region?.name;
        const locationName =
          locationCity && locationState
            ? `${locationCity}, ${locationState}`
            : locationObject.properties?.name;

        await updateProfile({
          id: userId,
          location_name: locationName,
          location_object: locationObject,
        }).unwrap();
      }
    } catch (error) {
      console.error("[OnboardingLocation] Failed to fetch/save location:", error);
    } finally {
      setIsSavingLocation(false);
    }
  };

  const handleContinue = async () => {
    if (!navigator.geolocation) {
      onContinue();
      return;
    }

    try {
      // Use the Permissions API to check without prompting first
      const permResult = await navigator.permissions?.query({
        name: "geolocation",
      });

      if (permResult?.state === "granted") {
        setLocationPermissionGranted(true);
        await fetchAndSaveLocation();
        onContinue();
        return;
      }

      // This will trigger the browser's permission prompt
      await fetchAndSaveLocation();
      setLocationPermissionGranted(true);
      onContinue();
    } catch {
      // Permission denied or error — continue without saving
      setLocationPermissionGranted(false);
      onContinue();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto autohide-scrollbar px-8 pt-4 pb-8">
        <div className="flex flex-col gap-6 items-start">
          <Icon name="map-pin" size={32} className="text-text-primary" />

          <h1 className="text-h2 text-text-primary">
            Share your general location
          </h1>

          <p className="text-body-1 text-text-primary leading-relaxed">
            Your location helps us suggest nearby clubs and events. We
            never store your exact location.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-8 pb-6 pt-4">
        <button
          onClick={handleContinue}
          disabled={isSavingLocation}
          className={cn(
            "w-full py-3.5 rounded-full text-body-1-medium font-medium transition-opacity",
            !isSavingLocation
              ? "bg-text-primary text-text-reversed hover:opacity-90"
              : "bg-border-tertiary text-text-tertiary cursor-not-allowed"
          )}
        >
          {isSavingLocation ? "Updating..." : "Continue"}
        </button>
      </div>
    </div>
  );
}
